import { useCallback, useEffect, useState } from 'react';
import { AuthMode, AuthPayload, User } from '../domain';
import { fetchProfile, loginUser, registerUser } from '../services/authService';
import { extractErrorMessage } from '../utils/errors';

type UseAuthStateResult = {
  token: string | null;
  user: User | null;
  authMode: AuthMode;
  setAuthMode: (mode: AuthMode) => void;
  authLoading: boolean;
  authError: string | null;
  authModalOpen: boolean;
  openAuthModal: () => void;
  closeAuthModal: () => void;
  handleAuthSubmit: (payload: AuthPayload) => Promise<void>;
  handleLogout: () => void;
};

export function useAuthState(): UseAuthStateResult {
  const [token, setToken] = useState<string | null>(() => localStorage.getItem('ai-chat-token'));
  const [user, setUser] = useState<User | null>(null);
  const [authMode, setAuthMode] = useState<AuthMode>('login');
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [authModalOpen, setAuthModalOpen] = useState(false);

  const handleLogout = useCallback(() => {
    localStorage.removeItem('ai-chat-token');
    setToken(null);
    setUser(null);
  }, []);

  const closeAuthModal = useCallback(() => {
    setAuthModalOpen(false);
    setAuthError(null);
  }, []);

  const handleAuthSubmit = useCallback(
    async (payload: AuthPayload) => {
      setAuthLoading(true);
      setAuthError(null);
      try {
        if (authMode === 'register') {
          await registerUser(payload);
        }
        const loginResponse = await loginUser(payload);
        localStorage.setItem('ai-chat-token', loginResponse.access_token);
        setToken(loginResponse.access_token);
        closeAuthModal();
      } catch (error) {
        setAuthError(extractErrorMessage(error));
      } finally {
        setAuthLoading(false);
      }
    },
    [authMode, closeAuthModal]
  );

  useEffect(() => {
    let cancelled = false;
    async function loadProfile() {
      if (!token) {
        setUser(null);
        return;
      }
      try {
        const profile = await fetchProfile(token);
        if (!cancelled) {
          setUser(profile);
        }
      } catch (error) {
        console.error('Failed to sync profile', error);
        if (!cancelled) {
          handleLogout();
        }
      }
    }
    loadProfile();
    return () => {
      cancelled = true;
    };
  }, [token, handleLogout]);

  return {
    token,
    user,
    authMode,
    setAuthMode,
    authLoading,
    authError,
    authModalOpen,
    openAuthModal: () => setAuthModalOpen(true),
    closeAuthModal,
    handleAuthSubmit,
    handleLogout,
  };
}
